export const TODAY_SCROLL_LOOKBACK_MINUTES = 35;

type RowWithStartAndSlotId = {
  start_time: string;
  slots?: Array<{ tee_time_id?: number; division_id?: number }>;
};

function parseStartToMinutesSinceMidnight(startTime: string): number | undefined {
  const parts = String(startTime ?? "").split(":");
  if (parts.length < 2) {
    return undefined;
  }
  const h = Number.parseInt(parts[0], 10);
  const m = Number.parseInt(parts[1], 10);
  if (Number.isNaN(h) || Number.isNaN(m)) {
    return undefined;
  }
  return h * 60 + m;
}

function isBetterAnchorMatch(
  dist: number,
  slotM: number,
  slotDivisionId: number | undefined,
  anchor: number,
  bestDist: number,
  bestSlotM: number,
  bestDivisionId: number | undefined,
  preferredDivisionId?: number,
): boolean {
  if (dist < bestDist) {
    return true;
  }
  if (dist > bestDist) {
    return false;
  }
  if (preferredDivisionId != null) {
    const preferred = slotDivisionId === preferredDivisionId;
    const bestPreferred = bestDivisionId === preferredDivisionId;
    if (preferred && !bestPreferred) {
      return true;
    }
    if (!preferred && bestPreferred) {
      return false;
    }
  }
  const before = slotM <= anchor;
  const bestBefore = bestSlotM <= anchor;
  if (before && !bestBefore) {
    return true;
  }
  if (!before && bestBefore) {
    return false;
  }
  if (before && bestBefore) {
    return slotM > bestSlotM;
  }
  return slotM < bestSlotM;
}

export function findTeeTimeIdClosestToLookbackAnchor<T extends RowWithStartAndSlotId>(
  sorted: T[],
  preferredDivisionId?: number,
): number | undefined {
  if (sorted.length === 0) {
    return undefined;
  }
  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  let anchorMinutes = nowMinutes - TODAY_SCROLL_LOOKBACK_MINUTES;
  if (anchorMinutes < 0) {
    anchorMinutes = 0;
  }

  let bestId: number | undefined;
  let bestDist = Infinity;
  let bestSlotM = -1;
  let bestDivisionId: number | undefined;

  for (const row of sorted) {
    const slotM = parseStartToMinutesSinceMidnight(row.start_time);
    if (slotM === undefined) {
      continue;
    }
    const id = row.slots?.[0]?.tee_time_id;
    if (id == null) {
      continue;
    }
    const slotDivisionId = row.slots?.[0]?.division_id;
    const dist = Math.abs(slotM - anchorMinutes);
    if (
      bestId === undefined ||
      isBetterAnchorMatch(
        dist,
        slotM,
        slotDivisionId,
        anchorMinutes,
        bestDist,
        bestSlotM,
        bestDivisionId,
        preferredDivisionId,
      )
    ) {
      bestDist = dist;
      bestSlotM = slotM;
      bestId = id;
      bestDivisionId = slotDivisionId;
    }
  }

  return bestId;
}
